/** Copyright (c) 2021 T. Rowe Price. All rights reserved. */

import { assign, isEmpty, keyBy } from 'lodash';
import { createSelector } from 'reselect';

import { isActiveAdvisory } from 'services/Products';
import { extractAndFormatOwners } from 'services/Accounts';
import { aggregateValues, sumAggregates } from 'services/Calculation';
import { getSelections } from 'state/selections/SelectionsSelectors';
import { getActivitySummaryFilter } from './ActivitySummaryFilterSelectors';
import { getPerformancePage } from './PerformancePageSelectors';
import { applyPerformanceFilters } from './PerformancePageUtils';
import { accountName, formatAccountNumber } from 'services/Formats';
import { getAccounts } from '../account/AccountSelectors';

const getHistory = (state) => state.history || [];

export const getAccountsActivitySummary = createSelector(
  getPerformancePage,
  getActivitySummaryFilter,
  getHistory,
  getAccounts,
  (performancePage, activitySummaryFilter, history, accounts) => {
    // aggregate selected account history
    const filteredHistory = applyPerformanceFilters(
      performancePage,
      activitySummaryFilter,
      history
    );

    const accountLookup = keyBy(accounts, 'accountId');

    const result = filteredHistory.map((h) => {
      const account = accountLookup[h.accountId];
      const accountInfo = {
        accountNumber: formatAccountNumber(account),
        accountType: account.accountType,
        isViewOnly: account.isViewOnly,
        retirementAccount: account.retirementAccount,
        accountDescriptor: accountName(account),
        owners: account.owners,
        balanceAsOfDate: account.balanceAsOfDate
      };
      const dailyAggregate = aggregateValues(h);
      const accountAggregate = dailyAggregate;
      const productType = { productType: account.productType };
      const accountId = { accountId: h.accountId };
      return assign({}, accountInfo, accountAggregate, productType, accountId);
    });

    return result;
  }
);

export const buildAccountsActivitySummary = createSelector(
  getAccountsActivitySummary,
  (selectedAccounts) => {
    if (isEmpty(selectedAccounts)) {
      return [];
    }

    return selectedAccounts.map((account) => {
      const {
        productType,
        accountType,
        accountDescriptor,
        accountNumber
      } = account;

      const personalInvestment = 'Personal Investments';
      const managementType = isActiveAdvisory(productType);
      const ownerNames = extractAndFormatOwners(account);
      const typeAndOwners = isEmpty(ownerNames)
        ? `${accountType}`
        : `${accountType}\n${ownerNames}`;
      const accountNameAndNumber = `${accountDescriptor}\n${accountNumber}`;

      const result = {
        personalInvestment,
        managementType,
        accountNameAndNumber,
        typeAndOwners,
        accountNumber,
        ...account
      };

      return result;
    });
  }
);

export const getActivitySummaryForPerformance = createSelector(
  getActivitySummaryFilter,
  getSelections,
  getHistory,
  (filter, selections, history) => {
    const { subset } = selections.accountsPreFilter;
    const aggregates = history.filter((h) => subset[h.accountId]);

    const filteredAggregates = aggregates.filter(
      (a) => filter.accountIds[a.accountId] !== null
    );

    return sumAggregates(filteredAggregates);
  }
);
